import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  listHousing,
  listNotices,
  listStandards,
  listTaxForms,
  listTranscriptTypes,
  listTransactionCodeGroups,
  listTransactionCodes,
  listVehicleRegions,
} from "@/features/reference/api";

interface DatasetCard {
  title: string;
  description: string;
  to: string;
  count: number | null;
}

/** Effective vintage badge — the newest effective_date across the standards. */
function latestVintage(dates: string[]): string | null {
  return dates.length ? dates.reduce((a, b) => (a > b ? a : b)) : null;
}

export function ReferenceHome() {
  const [cards, setCards] = useState<DatasetCard[] | null>(null);
  const [vintage, setVintage] = useState<string | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    Promise.all([
      listNotices({ pageSize: 1 }),
      listTaxForms({ pageSize: 1 }),
      listStandards({ pageSize: 500 }),
      listHousing({ pageSize: 1 }),
      listVehicleRegions(),
      listTransactionCodes({ pageSize: 1 }),
      listTransactionCodeGroups(),
      listTranscriptTypes(),
    ])
      .then(([notices, forms, standards, housing, vehicle, codes, groups, transcripts]) => {
        if (!active) return;
        setVintage(latestVintage(standards.items.map((s) => s.effectiveDate)));
        setCards([
          {
            title: "Notice Dictionary",
            description: "What each IRS notice code means and the recommended action.",
            to: "/settings/reference/notices",
            count: notices.count,
          },
          {
            title: "Tax-Form Registry",
            description: "Forms the practice fills, their categories and applicability.",
            to: "/settings/reference/tax-forms",
            count: forms.count,
          },
          {
            title: "Collection Financial Standards",
            description: "National, housing, vehicle & other monthly allowances.",
            to: "/settings/reference/standards",
            count: standards.count + housing.count + vehicle.count,
          },
          {
            title: "Transaction Codes",
            description: `Account-transcript code vocabulary and ${groups.length} analysis buckets.`,
            to: "/settings/reference/transaction-codes",
            count: codes.count,
          },
          {
            title: "Transcript Types",
            description: "Row-type dictionary (populated in Tier 2.3 Transcripts).",
            to: "/settings/reference/transcript-types",
            count: transcripts.length,
          },
        ]);
      })
      .catch(() => active && setError(true));
    return () => {
      active = false;
    };
  }, []);

  if (error) {
    return (
      <p className="text-destructive text-sm">Couldn&apos;t load reference data. Please refresh.</p>
    );
  }

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {(cards ?? Array.from({ length: 5 }, () => null)).map((card, i) =>
        card ? (
          <Link key={card.title} to={card.to} className="block">
            <Card className="hover:shadow-card-hover h-full transition-shadow">
              <CardHeader>
                <div className="flex items-start justify-between gap-2">
                  <CardTitle className="text-base">{card.title}</CardTitle>
                  <Badge variant="secondary" className="tabular-nums">
                    {card.count ?? "—"}
                  </Badge>
                </div>
                <CardDescription>{card.description}</CardDescription>
              </CardHeader>
              {card.title.startsWith("Collection") && vintage && (
                <CardContent>
                  <Badge variant="outline">Effective vintage: {vintage}</Badge>
                </CardContent>
              )}
            </Card>
          </Link>
        ) : (
          <Card key={i} className="h-28 animate-pulse" />
        ),
      )}
    </div>
  );
}
